'use client';

/**
 * Динамика среднего балла.
 *
 * Ряды приходят без пропусков в датах: бакет без звонков — точка с нулями, а
 * не отсутствующая дата. График с дырами читается как сбой сбора данных,
 * хотя в этот день просто не звонили.
 *
 * `avgScore: null` в точке — **разрыв линии**, а не ноль: `connectNulls`
 * выключен намеренно. Соединив концы через пустой день, линия соврала бы,
 * что качество в этот день было где-то посередине.
 *
 * Отсюда следствие, из-за которого день пропадал с экрана: **линия без
 * соседей не рисуется вовсе.** День, у которого и слева, и справа пусто, —
 * это отрезок нулевой длины, и при выключенных точках от него не остаётся
 * ничего: на экране пусто, хотя звонки в этот день оценивались. Поэтому такие
 * дни рисуются точкой, а остальные — нет: на тридцати днях сплошные точки
 * сливаются в пунктир и прячут саму линию.
 *
 * Целевая линия — из ответа (`target`). Своей константы здесь нет: гадать, на
 * каком уровне у клиента поставлена цель, хуже, чем не рисовать её вовсе.
 */

import React from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartTooltip, axisProps, gridProps, seriesColors } from './chartTheme';
import type { ScoreDynamicsData } from '@/lib/api/types';

const dayFormat = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short' });

function shortDate(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : dayFormat.format(date);
}

interface Row {
  date: string;
  [key: string]: string | number | null;
}

export default function ScoreDynamicsChart({
  data,
  height = 260,
}: {
  data: ScoreDynamicsData;
  height?: number;
}) {
  /**
   * Ряды сводим в одну таблицу по дате. Даты у всех рядов одни и те же —
   * сервер их выравнивает, — поэтому берём сетку из первого ряда.
   */
  const dates = data.series[0]?.points.map((point) => point.date) ?? [];
  const rows: Row[] = dates.map((date, index) => {
    const row: Row = { date: shortDate(date) };
    for (const series of data.series) {
      const point = series.points[index];
      row[series.key] = point ? point.avgScore : null;
    }
    return row;
  });

  /**
   * Точка для дня, у которого нет соседей.
   *
   * Recharts рисует ряд отрезками между соседними непустыми значениями: у
   * одинокого дня соседей нет, отрезок вырождается, и при `dot={false}` день
   * пропадает с графика целиком. Для него — кружок, для остальных ничего:
   * иначе точки на длинном периоде сливаются в пунктир.
   */
  const lonelyDot = (key: string) =>
    function LonelyDot(props: {
      cx?: number | null;
      cy?: number | null;
      index?: number;
      stroke?: string;
    }) {
      const { cx, cy, index, stroke } = props;
      if (index === undefined) return <g />;

      const has = (position: number) => {
        const value = rows[position]?.[key];
        return value !== null && value !== undefined;
      };

      /* У дня без оценки значения нет, но рисователь точки всё равно
         вызывается — и координата приходит пустой. Нарисованная по ней точка
         садится к верхнему краю, то есть на сотню баллов: день, который не
         оценивали, выглядел отличником. Поэтому сначала смотрим на само
         значение, а не на координаты. */
      if (!has(index)) return <g />;
      if (cx === undefined || cx === null || cy === undefined || cy === null) return <g />;
      if (has(index - 1) || has(index + 1)) return <g />;

      return <circle cx={cx} cy={cy} r={3} fill={stroke} stroke={stroke} />;
    };

  const scored = data.series.reduce(
    (sum, series) => sum + series.points.reduce((inner, point) => inner + point.scoredCalls, 0),
    0
  );

  if (scored === 0) {
    return (
      <div
        className="flex items-center justify-center text-xs text-slate-400 text-center px-6"
        style={{ height }}
      >
        За период не оценено ни одного звонка — рисовать нечего. Это не ноль баллов: проверка не
        проводилась.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={rows} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="date" {...axisProps} />
        <YAxis domain={[0, 100]} {...axisProps} />
        <Tooltip content={<ChartTooltip emptyLabel="не оценивалось" />} />
        {data.series.length > 1 && (
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" iconSize={7} />
        )}
        <ReferenceLine y={data.target} stroke="#16a34a" strokeDasharray="4 4" />
        {data.series.map((series, index) => (
          <Line
            key={series.key}
            type="monotone"
            dataKey={series.key}
            name={series.name}
            stroke={seriesColors[index % seriesColors.length]}
            strokeWidth={2}
            /* Точка только там, где линии не будет: у дня без соседей
               отрезок нулевой длины, и без точки он исчезает с экрана. */
            dot={lonelyDot(series.key)}
            activeDot={{ r: 4 }}
            /* Пустой день — разрыв, а не соединение через него. */
            connectNulls={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
